import { FC, useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperObj } from 'swiper'
import cx from 'classnames'

import styles from './StoriesModal.module.scss'

import Arrow from '../../../../public/static/icons/arrow-white.svg'
import Close from './close.svg'
import Image from '../../../common/Image/Image'
import { IStories } from '../../../types'

interface IStoriesModal {
  storyOpen: string
  setStoryOpen: React.Dispatch<React.SetStateAction<string | undefined>>
  stories: IStories[]
}

const STORY_DURATION = 15000
const STORY_DURATION_STEPS = 100

const StoriesModal: FC<IStoriesModal> = ({
  storyOpen,
  setStoryOpen,
  stories,
}) => {
  const timer = useRef<NodeJS.Timeout>()
  const interval = useRef<NodeJS.Timeout>()
  const wrapperRef = useRef(null)
  const [swiper, setSwiper] = useState<SwiperObj>()

  const initialStoryIndex = stories.findIndex((s) => s.id === storyOpen)
  const [activeIndex, setActiveIndex] = useState(
    initialStoryIndex >= 0 ? initialStoryIndex : 0,
  )
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  // 0 - 100
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (storyOpen) {
      setActiveIndex(initialStoryIndex)
      disableBodyScroll(wrapperRef.current)
    } else {
      clearAllBodyScrollLocks()
    }
  }, [storyOpen])

  useEffect(() => {
    return () => {
      clearAllBodyScrollLocks()
      if (timer.current) {
        clearTimeout(timer.current)
      }
      if (interval.current) {
        clearInterval(interval.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!swiper || activeIndex >= stories.length || !storyOpen) {
      return
    }

    interval.current = setInterval(() => {
      setProgress((p) => p + 100 / STORY_DURATION_STEPS)
    }, STORY_DURATION / STORY_DURATION_STEPS)

    timer.current = setTimeout(() => {
      const totalSlides = stories[activeIndex].detailImg.length
      if (activeSlideIndex < totalSlides - 1) {
        setActiveSlideIndex(activeSlideIndex + 1)
      } else {
        swiper.slideNext()
        if (activeIndex < stories.length - 1) {
          setActiveSlideIndex(0)
        }
      }
    }, STORY_DURATION)

    return () => {
      setProgress(0)
      if (timer.current) {
        clearTimeout(timer.current)
      }
      if (interval.current) {
        clearInterval(interval.current)
      }
    }
  }, [activeIndex, activeSlideIndex, swiper, storyOpen])

  const options: Swiper = {
    initialSlide: activeIndex,
    threshold: 2,
    loop: true,
    loopedSlides: 3,
    onSlideChange: (swiper) => {
      setActiveIndex(swiper.realIndex)
    },
    onSwiper: (s) => {
      setSwiper(s)
    },
  }

  if (!stories.length) {
    return null
  }

  function renderProgress() {
    return (
      <ul className={styles.progress}>
        {stories[activeIndex].detailImg.map((image, index) => (
          <li
            key={image.id}
            className={styles.progressButton}
            onClick={() => {
              setActiveSlideIndex(index)
            }}
          >
            <div className={styles.progressItem}>
              <div
                className={styles.progressBar}
                style={{
                  transitionDuration:
                    progress > 0 && index === activeSlideIndex
                      ? `${STORY_DURATION / STORY_DURATION_STEPS}ms`
                      : '0s',
                  transform: `translate3d(${
                    index > activeSlideIndex ||
                    (index > activeSlideIndex && progress === 0)
                      ? '-101%'
                      : index === activeSlideIndex
                      ? `-${100 - progress}%`
                      : '0%'
                  }, 0, 0)`,
                }}
              />
            </div>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div ref={wrapperRef}>
      <Modal
        isOpen={!!storyOpen}
        overlayClassName={cx(styles.modal, {
          storyOpen: styles.modalOpen,
        })}
        className={styles.modalContent}
      >
        <button
          type="button"
          className={cx(styles.button, styles.buttonClose)}
          onClick={() => {
            setStoryOpen(undefined)
          }}
        >
          <Close />
        </button>
        <div className={styles.flexWrapper}>
          <div className={styles.wrapper}>
            <button
              type="button"
              className={cx(styles.button, styles.buttonPrev)}
              onClick={() => {
                setActiveSlideIndex(0)
                swiper.slidePrev()
              }}
            >
              <Arrow />
            </button>
            <button
              type="button"
              className={cx(styles.button, styles.buttonNext)}
              onClick={() => {
                setActiveSlideIndex(0)
                swiper.slideNext()
              }}
            >
              <Arrow />
            </button>
            {renderProgress()}
            <Swiper {...options} className={styles.slider}>
              {stories.map((story, index) => {
                const distanceToActiveSlide = index - activeIndex

                return (
                  <SwiperSlide
                    key={story.id}
                    onClick={() => {
                      if (activeIndex !== index) {
                        setActiveSlideIndex(0)
                        swiper.slideToLoop(index)
                      }
                    }}
                    className={cx(styles.slide, {
                      [styles.slideClickable]:
                        Math.abs(distanceToActiveSlide) === 1,
                    })}
                  >
                    <Image
                      className={styles.image}
                      src={
                        story.detailImg[
                          activeIndex === index ? activeSlideIndex : 0
                        ].url
                      }
                      alt={story.title}
                      priority
                      layout="fixed"
                      width={380}
                      height={670}
                    />
                  </SwiperSlide>
                )
              })}
            </Swiper>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default StoriesModal
